<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProvidersStore } from '@/store/providers'
import Progress from '@/components/forms/Progress.vue'
import { getFirstFieldError, normalizeApiError } from '@/utils/apiError'

const route = useRoute()
const router = useRouter()
const store = useProvidersStore()

const id = computed(() => route.params.id)
const isNew = computed(() => id.value === 'new')

const name = ref('')
const type = ref('')
const exchangeRate = ref('')
const markup = ref('')
const errorMessage = ref('')
const fieldErrors = ref({})

function clearFeedback() {
  errorMessage.value = ''
  fieldErrors.value = {}
}

function getFieldError(fieldName) {
  return getFirstFieldError(fieldErrors.value, fieldName)
}

function applyApiError(error, fallbackMessage) {
  const apiError = normalizeApiError(error, { fallbackMessage })
  errorMessage.value = apiError.message
  fieldErrors.value = apiError.fieldErrors
}

function resetForm() {
  name.value = ''
  exchangeRate.value = ''
  markup.value = ''
}

function fillForm(provider = {}) {
  name.value = provider.name || ''
  exchangeRate.value = provider.exchangeRate ?? ''
  markup.value = provider.markup ?? ''
}

async function loadProvider() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = ''
    return
  }

  type.value = 'loading'
  try {
    await store.getProvider(id.value)
    fillForm(store.currentProvider || {})
    type.value = ''
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося завантажити постачальника.')
  }
}

async function updateProvider() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = 'success'
    return
  }

  type.value = 'loading'
  try {
    await store.getProvider(id.value)
    fillForm(store.currentProvider || {})
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося оновити дані форми.')
  }
}

async function saveProvider() {
  type.value = 'loading'
  clearFeedback()

  const sendData = {}

  if (name.value) sendData.name = name.value
  if (exchangeRate.value !== '' && !Number.isNaN(Number(exchangeRate.value))) sendData.exchangeRate = Number(exchangeRate.value)
  if (markup.value !== '' && !Number.isNaN(Number(markup.value))) sendData.markup = Number(markup.value)

  try {
    if (isNew.value) {
      const created = await store.addProvider(sendData)
      const createdId = created._id?.$oid || created._id || created.id
      type.value = 'success'
      if (createdId) {
        await router.replace(`/providers/${createdId}`)
      }
      return
    }

    await store.updateProvider(id.value, sendData)
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося зберегти постачальника.')
  }
}

onMounted(loadProvider)
watch(() => route.params.id, loadProvider)
</script>

<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>{{ route.meta.title }}</h2>
      <Progress :type="type" :error-message="errorMessage" />
      <div>
        <button class="btn btn-outline-secondary me-2" @click="updateProvider" :disabled="type === 'loading'">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button class="btn btn-primary" @click="saveProvider" :disabled="type === 'loading'">
          <i class="bi bi-save"></i>
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">
            Назва
          </div>
          <div class="col-md-8">
            <input
              v-model="name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': !!getFieldError('name') }"
            />
            <div v-if="getFieldError('name')" class="invalid-feedback d-block">
              {{ getFieldError('name') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">
            Курс
          </div>
          <div class="col-md-8">
            <input
              v-model="exchangeRate"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': !!getFieldError('exchangeRate') }"
            />
            <div v-if="getFieldError('exchangeRate')" class="invalid-feedback d-block">
              {{ getFieldError('exchangeRate') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">
            Націнка
          </div>
          <div class="col-md-8">
            <input
              v-model="markup"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': !!getFieldError('markup') }"
            />
            <div v-if="getFieldError('markup')" class="invalid-feedback d-block">
              {{ getFieldError('markup') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
